import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import type { ReactNode } from "react";

export interface WorkflowRunsFilter {
  key: string;
  label: string;
  options: { value: string | number; label: string }[];
  value: string | number | "";
  onChange: (value: string | number | "") => void;
  allLabel?: string;
}

export interface WorkflowRunsColumn<TRow> {
  key: string;
  header: ReactNode;
  align?: "left" | "right" | "center";
  render: (row: TRow) => ReactNode;
}

export interface WorkflowRunsPageProps<TRow> {
  title: string;
  testIdPrefix: string;
  filters: WorkflowRunsFilter[];
  columns: WorkflowRunsColumn<TRow>[];
  rows: TRow[];
  rowKey: (row: TRow) => string | number;
  isLoading: boolean;
  /** The runs query failed: render an error row instead of the empty state. */
  isError?: boolean;
  onRefresh: () => void;
  onRowClick?: (row: TRow) => void;
  emptyState: ReactNode;
  /** Adapter-owned detail surface (usually a Dialog), rendered after the table. */
  detail?: ReactNode;
}

/**
 * The status-bearing "past runs" frame every workflow product shares: title
 * row with select filters and refresh, a compact table, an empty state, and
 * an adapter-owned detail surface. What a run IS — its columns, status
 * derivation, and detail view — differs per product (a 50.59 record's status
 * enum and a PG run's stage completion are different truths), so those come
 * in as props rather than a generic backend list.
 */
const WorkflowRunsPage = <TRow,>({
  title,
  testIdPrefix,
  filters,
  columns,
  rows,
  rowKey,
  isLoading,
  onRefresh,
  onRowClick,
  emptyState,
  detail,
  isError = false,
}: WorkflowRunsPageProps<TRow>) => {
  return (
    <Box sx={{ p: 3 }} data-testid={`${testIdPrefix}-runs-page`}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {title}
        </Typography>
        {filters.map((filter) => (
          <FormControl key={filter.key} size="small" sx={{ minWidth: 170 }}>
            <InputLabel>{filter.label}</InputLabel>
            <Select
              label={filter.label}
              value={filter.value}
              onChange={(e) =>
                filter.onChange(e.target.value as string | number | "")
              }
            >
              <MenuItem value="">{filter.allLabel ?? `All`}</MenuItem>
              {filter.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
        <Tooltip title="Refresh">
          <IconButton onClick={onRefresh} disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Owns the horizontal scroll: the app shell sets overflow-x hidden
          on .c-wrapper, so without a container a wide table is clipped with
          no scrollbar and the rightmost columns silently disappear. */}
      <TableContainer>
        <Table size="small" data-testid={`${testIdPrefix}-runs-table`}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} align={column.align}>
                  {column.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              // One call per row: the React key and the testid must agree, and
              // an adapter's rowKey is arbitrary caller code.
              const key = rowKey(row);
              return (
                <TableRow
                  key={key}
                  hover={!!onRowClick}
                  sx={onRowClick ? { cursor: "pointer" } : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  data-testid={`${testIdPrefix}-run-row-${key}`}
                >
                  {columns.map((column) => (
                    <TableCell key={column.key} align={column.align}>
                      {column.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {!isLoading && isError && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography
                    variant="body2"
                    color="error"
                    component="div"
                    sx={{ py: 3, textAlign: "center" }}
                    data-testid={`${testIdPrefix}-runs-error`}
                  >
                    The runs list could not be loaded. Use refresh to try again.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="div"
                    sx={{ py: 3, textAlign: "center" }}
                  >
                    {emptyState}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {detail}
    </Box>
  );
};

export default WorkflowRunsPage;
